/**
 * IdentityProviders — Admin panel for external identity provider configuration
 *
 * Providers:
 *  1. Microsoft Azure AD (Entra ID) — OIDC/OAuth2
 *  2. Google Workspace — OIDC/OAuth2
 *  3. SAML 2.0 / eIDAS — SAML federation
 *
 * Sub-panels per provider:
 *  - Configuration: enable/disable, set credentials
 *  - Group Mappings: CRUD for IdP group → Eurion role
 *  - Provisioned Users: list, search, manual sync
 */
import { useState, useEffect, useCallback } from 'react';

const API = (import.meta as { env: Record<string, string> }).env['VITE_API_URL'] ?? '';

/** api.<domain> / app.<domain> of the deployment this admin portal runs on (admin.<domain>). */
function siblingOrigin(sub: 'api' | 'app'): string {
  const { protocol, host } = window.location;
  return `${protocol}//${sub}.${host.replace(/^admin./, '')}`;
}
const API_ORIGIN = siblingOrigin('api');
const APP_ORIGIN = siblingOrigin('app');

// ── Shared fetch helpers ──────────────────────────────────────────
async function apiGet<T>(path: string): Promise<T> {
  const token = localStorage.getItem('eurion_token');
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json() as { success: boolean; data: T; error?: { message: string } };
  if (!json.success) throw new Error(json.error?.message ?? 'Request failed');
  return json.data;
}

async function apiMutate<T>(path: string, method: string, body?: unknown): Promise<T> {
  const token = localStorage.getItem('eurion_token');
  const res = await fetch(`${API}${path}`, {
    method,
    // Content-Type only with a body: on an empty DELETE/POST (sync, remove mapping) it made
    // Fastify answer 400 FST_ERR_CTP_EMPTY_JSON_BODY
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json() as { success: boolean; data: T; error?: { message: string } };
  if (!json.success) throw new Error(json.error?.message ?? 'Request failed');
  return json.data;
}

// ── Types ─────────────────────────────────────────────────────────
interface AzureConfig {
  id?: string;
  enabled: boolean;
  tenant_id?: string;
  client_id?: string;
  has_secret?: boolean;
  redirect_uri?: string;
  logout_redirect?: string;
  scopes?: string[];
  group_claim?: string;
  default_org_id?: string;
  default_roles?: string[];
  last_sync_at?: string | null;
  updated_at?: string;
}

interface GoogleConfig {
  id?: string;
  enabled: boolean;
  client_id?: string;
  has_secret?: boolean;
  hd_domain?: string;
  redirect_uri?: string;
  default_roles?: string[];
  last_sync_at?: string | null;
}

interface SamlConfig {
  id?: string;
  enabled: boolean;
  entity_id?: string;
  sso_url?: string;
  slo_url?: string;
  has_certificate?: boolean;
  name_id_format?: string;
  assertion_consumer_url?: string;
  default_roles?: string[];
}

interface GroupMapping {
  id: string;
  azure_group_id: string;
  azure_group_name: string | null;
  eurion_role: string;
  created_at: string;
}

interface ProvisionedUser {
  user_id: string;
  email: string;
  display_name: string | null;
  azure_groups: string[];
  last_sync_at: string | null;
  created_at: string;
  user_roles: string[];
  user_status: string;
}

// ── Shared UI helpers ─────────────────────────────────────────────
const inputCls = 'w-full rounded-lg border border-nord-3 bg-nord-0 px-3 py-2 text-sm text-nord-6 placeholder-nord-3 focus:outline-none focus:border-nord-8 transition-colors';
const labelCls = 'block text-xs text-nord-4 mb-1 font-medium';
const cardCls = 'bg-nord-1 border border-nord-2 rounded-xl p-5';
const ROLES = ['member', 'developer', 'admin', 'super-admin', 'moderator', 'guest'];

function StatusBadge({ enabled }: { enabled: boolean }) {
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${enabled ? 'bg-nord-14/15 text-nord-14' : 'bg-nord-3/30 text-nord-4'}`}>
      {enabled ? '● Enabled' : '○ Disabled'}
    </span>
  );
}

function ErrorBanner({ error }: { error: string | null }) {
  if (!error) return null;
  return <div className="text-nord-11 text-sm bg-nord-11/10 border border-nord-11/20 rounded-lg p-3">{error}</div>;
}

function SuccessBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return <div className="text-nord-14 text-sm bg-nord-14/10 border border-nord-14/20 rounded-lg p-3">{message}</div>;
}

function ToggleSwitch({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-nord-8' : 'bg-nord-3'}`}
    >
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${enabled ? 'left-6' : 'left-1'}`} />
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AZURE AD PANELS
// ═══════════════════════════════════════════════════════════════════

function AzureConfigPanel() {
  const [cfg, setCfg] = useState<AzureConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState(`${API_ORIGIN}/v1/auth/azure/callback`);
  const [logoutUri, setLogoutUri] = useState(`${APP_ORIGIN}`);
  const [groupClaim, setGroupClaim] = useState('groups');
  const [defaultRoles, setDefaultRoles] = useState('member');

  useEffect(() => {
    setLoading(true);
    apiGet<{ config: AzureConfig }>('/v1/admin/azure/config')
      .then(d => {
        const c = d.config;
        setCfg(c);
        setEnabled(c.enabled ?? false);
        setTenantId(c.tenant_id ?? '');
        setClientId(c.client_id ?? '');
        setRedirectUri(c.redirect_uri ?? `${API_ORIGIN}/v1/auth/azure/callback`);
        setLogoutUri(c.logout_redirect ?? `${APP_ORIGIN}`);
        setGroupClaim(c.group_claim ?? 'groups');
        setDefaultRoles((c.default_roles ?? ['member']).join(', '));
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload: Record<string, unknown> = {
        enabled, tenantId, clientId, redirectUri,
        logoutRedirect: logoutUri, groupClaim,
        defaultRoles: defaultRoles.split(',').map(r => r.trim()).filter(Boolean),
      };
      if (clientSecret) payload['clientSecret'] = clientSecret;
      await apiMutate('/v1/admin/azure/config', 'PUT', payload);
      setSuccess('Azure AD configuration saved.');
      setClientSecret('');
    } catch (e) { setError(String(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-nord-4 p-4">Loading configuration…</div>;

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-nord-8/15 flex items-center justify-center text-lg">🔷</div>
        <div className="flex-1">
          <h3 className="font-semibold text-nord-6">Microsoft Azure AD (Entra ID)</h3>
          <p className="text-xs text-nord-4">OIDC/OAuth2 enterprise single sign-on</p>
        </div>
        <StatusBadge enabled={enabled} />
      </div>

      <ErrorBanner error={error} />
      <SuccessBanner message={success} />

      <div className={`${cardCls} flex items-center gap-3`}>
        <div className="flex-1">
          <div className="text-sm font-medium text-nord-6">Enable Azure AD Sign-in</div>
          <div className="text-xs text-nord-4 mt-0.5">Allow users to authenticate with their Microsoft accounts</div>
        </div>
        <ToggleSwitch enabled={enabled} onChange={() => setEnabled(e => !e)} />
      </div>

      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">App Registration</h4>
        <div>
          <label className={labelCls}>Tenant ID *</label>
          <input className={inputCls} value={tenantId} onChange={e => setTenantId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
          <p className="text-xs text-nord-3 mt-1">Azure Portal → App Registration → Overview</p>
        </div>
        <div>
          <label className={labelCls}>Client ID (Application ID) *</label>
          <input className={inputCls} value={clientId} onChange={e => setClientId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </div>
        <div>
          <label className={labelCls}>
            Client Secret {cfg?.has_secret ? <span className="text-nord-14">(set — leave blank to keep)</span> : <span className="text-nord-11">(not set)</span>}
          </label>
          <input className={inputCls} type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
            placeholder={cfg?.has_secret ? '••••••••••••••••' : 'Paste client secret…'} />
        </div>
      </div>

      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">Redirect URIs</h4>
        <div>
          <label className={labelCls}>Callback URI *</label>
          <input className={inputCls} value={redirectUri} onChange={e => setRedirectUri(e.target.value)} />
          <p className="text-xs text-nord-3 mt-1">Add this to Azure App Registration → Authentication → Redirect URIs</p>
        </div>
        <div>
          <label className={labelCls}>Post-logout Redirect URI</label>
          <input className={inputCls} value={logoutUri} onChange={e => setLogoutUri(e.target.value)} />
        </div>
      </div>

      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">Advanced</h4>
        <div>
          <label className={labelCls}>Groups Claim Name</label>
          <input className={inputCls} value={groupClaim} onChange={e => setGroupClaim(e.target.value)} placeholder="groups" />
          <p className="text-xs text-nord-3 mt-1">JWT claim containing group Object IDs</p>
        </div>
        <div>
          <label className={labelCls}>Default Roles (comma-separated)</label>
          <input className={inputCls} value={defaultRoles} onChange={e => setDefaultRoles(e.target.value)} placeholder="member" />
        </div>
      </div>

      {tenantId && (
        <div className="bg-nord-0 border border-nord-2 rounded-lg p-3 font-mono text-xs text-nord-4">
          <div className="text-nord-3 mb-1">Authority URL:</div>
          <div>https://login.microsoftonline.com/{tenantId}/v2.0</div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="px-5 py-2.5 bg-nord-10 hover:bg-nord-10/80 text-nord-6 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
        {saving ? 'Saving…' : 'Save Configuration'}
      </button>
    </div>
  );
}

function AzureGroupMappingsPanel() {
  const [mappings, setMappings] = useState<GroupMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [newGroupId, setNewGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiGet<{ items: GroupMapping[] }>('/v1/admin/azure/mappings')
      .then(d => setMappings(d.items))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMapping = async () => {
    if (!newGroupId || !newRole) return;
    setAdding(true);
    try {
      await apiMutate('/v1/admin/azure/mappings', 'POST', {
        azureGroupId: newGroupId, azureGroupName: newGroupName || null, eurionRole: newRole,
      });
      setNewGroupId(''); setNewGroupName(''); setNewRole('member');
      load();
    } catch (e) { setError(String(e)); }
    finally { setAdding(false); }
  };

  const deleteMapping = async (id: string) => {
    if (!confirm('Delete this mapping?')) return;
    try { await apiMutate(`/v1/admin/azure/mappings/${id}`, 'DELETE'); load(); }
    catch (e) { setError(String(e)); }
  };

  const syncGroups = async () => {
    setSyncing(true); setError(null);
    try {
      const result = await apiMutate<{ synced: number; errors: number; total: number }>('/v1/admin/azure/sync', 'POST');
      alert(`Sync complete: ${result.synced}/${result.total} users updated, ${result.errors} errors.`);
    } catch (e) { setError(String(e)); }
    finally { setSyncing(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-nord-6">Azure Group → Eurion Role Mappings</h3>
          <p className="text-xs text-nord-4 mt-0.5">Map Azure AD groups to Eurion platform roles</p>
        </div>
        <button onClick={syncGroups} disabled={syncing}
          className="px-4 py-1.5 bg-nord-2 hover:bg-nord-3 rounded-lg text-sm text-nord-5 disabled:opacity-50 transition-colors">
          {syncing ? 'Syncing…' : '↻ Sync All Users'}
        </button>
      </div>

      <ErrorBanner error={error} />

      <div className={cardCls}>
        <h4 className="text-sm font-medium text-nord-5 mb-3">Add Mapping</h4>
        <div className="flex gap-2 flex-wrap">
          <input className={`${inputCls} flex-1 min-w-40`} placeholder="Azure Group Object ID"
            value={newGroupId} onChange={e => setNewGroupId(e.target.value)} />
          <input className={`${inputCls} flex-1 min-w-32`} placeholder="Display name (optional)"
            value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
          <select className={`${inputCls} w-36`} value={newRole} onChange={e => setNewRole(e.target.value)}>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={addMapping} disabled={adding || !newGroupId || !newRole}
            className="px-4 py-2 bg-nord-10 hover:bg-nord-10/80 rounded-lg text-sm text-white disabled:opacity-50 transition-colors">
            {adding ? 'Adding…' : '+ Add'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-nord-4 text-sm p-4">Loading mappings…</div>
      ) : mappings.length === 0 ? (
        <div className="text-center text-nord-4 text-sm py-10 bg-nord-1 border border-nord-2 rounded-xl">
          <div className="text-2xl mb-2">🗂️</div>
          No mappings configured. Add one above.
        </div>
      ) : (
        <div className="bg-nord-1 border border-nord-2 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nord-2 text-left text-xs text-nord-4">
                <th className="px-4 py-3 font-medium">Group</th>
                <th className="px-4 py-3 font-medium">Azure Group ID</th>
                <th className="px-4 py-3 font-medium">Eurion Role</th>
                <th className="px-4 py-3 font-medium">Added</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => (
                <tr key={m.id} className="border-b border-nord-2/50 hover:bg-nord-2/30 transition-colors">
                  <td className="px-4 py-3 text-nord-6 font-medium">{m.azure_group_name ?? <span className="text-nord-3 italic">unnamed</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs text-nord-4 max-w-48 truncate">{m.azure_group_id}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-nord-8/15 text-nord-8 rounded text-xs font-mono">{m.eurion_role}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-nord-4">{new Date(m.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => deleteMapping(m.id)} className="text-nord-11 hover:text-nord-11/70 text-xs transition-colors">Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-nord-0 border border-nord-2 rounded-lg p-4 text-xs text-nord-4 space-y-1">
        <div className="font-medium text-nord-5 mb-2">How Group Mapping Works</div>
        <div>1. User logs in via Azure AD → token includes <code className="text-nord-8">groups</code> claim</div>
        <div>2. Eurion maps each group ID to roles using this table</div>
        <div>3. All matched roles are merged and assigned to the user</div>
        <div>4. If no group match, user gets the <em>Default Roles</em> from Configuration</div>
      </div>
    </div>
  );
}

function AzureUsersPanel() {
  const [users, setUsers] = useState<ProvisionedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    apiGet<{ items: ProvisionedUser[]; total: number }>(`/v1/admin/azure/users?q=${encodeURIComponent(search)}&limit=50`)
      .then(d => { setUsers(d.items); setTotal(d.total); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [search]);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-nord-6">Azure AD Users</h3>
          <p className="text-xs text-nord-4 mt-0.5">{total} user{total !== 1 ? 's' : ''} provisioned via Azure AD</p>
        </div>
        <input className={`${inputCls} w-56`} placeholder="Search by email or name…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <ErrorBanner error={error} />

      {loading ? (
        <div className="text-nord-4 text-sm p-4">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="text-center text-nord-4 text-sm py-10 bg-nord-1 border border-nord-2 rounded-xl">
          <div className="text-2xl mb-2">👤</div>
          No Azure AD users found.
        </div>
      ) : (
        <div className="bg-nord-1 border border-nord-2 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nord-2 text-left text-xs text-nord-4">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Roles</th>
                <th className="px-4 py-3 font-medium">Groups</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Last Sync</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-nord-2/50 hover:bg-nord-2/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-nord-8/20 flex items-center justify-center text-nord-8 text-sm font-medium">
                        {(u.display_name ?? u.email)[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div>
                        <div className="text-nord-6 font-medium text-sm">{u.display_name ?? u.email}</div>
                        <div className="text-nord-4 text-xs">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.user_roles ?? []).map(r => (
                        <span key={r} className="px-1.5 py-0.5 bg-nord-8/15 text-nord-8 rounded text-xs">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-nord-4">{u.azure_groups?.length ?? 0} group{(u.azure_groups?.length ?? 0) !== 1 ? 's' : ''}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.user_status === 'active' ? 'bg-nord-14/15 text-nord-14' : 'bg-nord-3/30 text-nord-4'}`}>
                      {u.user_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-nord-4">{u.last_sync_at ? new Date(u.last_sync_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// GOOGLE WORKSPACE PANEL
// ═══════════════════════════════════════════════════════════════════

function GoogleConfigPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cfg, setCfg] = useState<GoogleConfig | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [hdDomain, setHdDomain] = useState('');
  const [redirectUri, setRedirectUri] = useState(`${API_ORIGIN}/v1/auth/google/callback`);
  const [defaultRoles, setDefaultRoles] = useState('member');

  useEffect(() => {
    setLoading(true);
    apiGet<{ config: GoogleConfig }>('/v1/admin/google/config')
      .then(d => {
        const c = d.config;
        setCfg(c);
        setEnabled(c.enabled ?? false);
        setClientId(c.client_id ?? '');
        setHdDomain(c.hd_domain ?? '');
        setRedirectUri(c.redirect_uri ?? `${API_ORIGIN}/v1/auth/google/callback`);
        setDefaultRoles((c.default_roles ?? ['member']).join(', '));
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload: Record<string, unknown> = {
        enabled, clientId, hdDomain, redirectUri,
        defaultRoles: defaultRoles.split(',').map(r => r.trim()).filter(Boolean),
      };
      if (clientSecret) payload['clientSecret'] = clientSecret;
      await apiMutate('/v1/admin/google/config', 'PUT', payload);
      setSuccess('Google Workspace configuration saved.');
      setClientSecret('');
    } catch (e) { setError(String(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-nord-4 p-4">Loading configuration…</div>;

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-nord-14/15 flex items-center justify-center text-lg">
          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-nord-6">Google Workspace</h3>
          <p className="text-xs text-nord-4">OIDC/OAuth2 single sign-on for Google organizations</p>
        </div>
        <StatusBadge enabled={enabled} />
      </div>

      <ErrorBanner error={error} />
      <SuccessBanner message={success} />

      <div className={`${cardCls} flex items-center gap-3`}>
        <div className="flex-1">
          <div className="text-sm font-medium text-nord-6">Enable Google Workspace Sign-in</div>
          <div className="text-xs text-nord-4 mt-0.5">Allow users to authenticate with their Google organization accounts</div>
        </div>
        <ToggleSwitch enabled={enabled} onChange={() => setEnabled(e => !e)} />
      </div>

      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">OAuth2 Credentials</h4>
        <div>
          <label className={labelCls}>Client ID *</label>
          <input className={inputCls} value={clientId} onChange={e => setClientId(e.target.value)}
            placeholder="xxxxxxxxxxxx.apps.googleusercontent.com" />
          <p className="text-xs text-nord-3 mt-1">Google Cloud Console → APIs &amp; Services → Credentials → OAuth 2.0 Client ID</p>
        </div>
        <div>
          <label className={labelCls}>
            Client Secret {cfg?.has_secret ? <span className="text-nord-14">(set — leave blank to keep)</span> : <span className="text-nord-11">(not set)</span>}
          </label>
          <input className={inputCls} type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
            placeholder={cfg?.has_secret ? '••••••••••••••••' : 'Paste client secret…'} />
        </div>
      </div>

      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">Domain Restriction</h4>
        <div>
          <label className={labelCls}>Hosted Domain (hd)</label>
          <input className={inputCls} value={hdDomain} onChange={e => setHdDomain(e.target.value)}
            placeholder="yourorg.com" />
          <p className="text-xs text-nord-3 mt-1">Restrict login to this Google Workspace domain. Leave blank to allow all.</p>
        </div>
        <div>
          <label className={labelCls}>Callback URI</label>
          <input className={inputCls} value={redirectUri} onChange={e => setRedirectUri(e.target.value)} />
          <p className="text-xs text-nord-3 mt-1">Add this to Google Cloud → Authorized redirect URIs</p>
        </div>
        <div>
          <label className={labelCls}>Default Roles (comma-separated)</label>
          <input className={inputCls} value={defaultRoles} onChange={e => setDefaultRoles(e.target.value)} placeholder="member" />
        </div>
      </div>

      <div className="bg-nord-0 border border-nord-2 rounded-lg p-3 text-xs text-nord-4 space-y-1">
        <div className="font-medium text-nord-5 mb-2">Setup Instructions</div>
        <div>1. Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" className="text-nord-8 underline">Google Cloud Console → Credentials</a></div>
        <div>2. Create an OAuth 2.0 Client ID (Web application type)</div>
        <div>3. Set <strong>Authorized redirect URIs</strong> to the Callback URI above</div>
        <div>4. Copy the Client ID and Secret here</div>
        <div>5. Enable the <em>Google Workspace</em> identity provider in your Eurion Admin</div>
      </div>

      <button onClick={save} disabled={saving}
        className="px-5 py-2.5 bg-nord-10 hover:bg-nord-10/80 text-nord-6 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
        {saving ? 'Saving…' : 'Save Configuration'}
      </button>
    </div>
  );
}

function GoogleUsersPanel() {
  const [users, setUsers] = useState<ProvisionedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    apiGet<{ items: ProvisionedUser[]; total: number }>(`/v1/admin/google/users?q=${encodeURIComponent(search)}&limit=50`)
      .then(d => { setUsers(d.items); setTotal(d.total); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [search]);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-nord-6">Google Workspace Users</h3>
          <p className="text-xs text-nord-4 mt-0.5">{total} user{total !== 1 ? 's' : ''} provisioned via Google</p>
        </div>
        <input className={`${inputCls} w-56`} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <ErrorBanner error={error} />

      {loading ? (
        <div className="text-nord-4 text-sm p-4">Loading…</div>
      ) : users.length === 0 ? (
        <div className="text-center text-nord-4 text-sm py-10 bg-nord-1 border border-nord-2 rounded-xl">
          <div className="text-2xl mb-2">👤</div>
          No Google Workspace users found.
        </div>
      ) : (
        <div className="bg-nord-1 border border-nord-2 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nord-2 text-left text-xs text-nord-4">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Roles</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Provisioned</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-nord-2/50 hover:bg-nord-2/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-nord-14/20 flex items-center justify-center text-nord-14 text-sm font-medium">
                        {(u.display_name ?? u.email)[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div>
                        <div className="text-nord-6 font-medium text-sm">{u.display_name ?? u.email}</div>
                        <div className="text-nord-4 text-xs">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.user_roles ?? []).map(r => (
                        <span key={r} className="px-1.5 py-0.5 bg-nord-14/15 text-nord-14 rounded text-xs">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.user_status === 'active' ? 'bg-nord-14/15 text-nord-14' : 'bg-nord-3/30 text-nord-4'}`}>
                      {u.user_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-nord-4">{new Date(u.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SAML 2.0 / eIDAS PANEL
// ═══════════════════════════════════════════════════════════════════

function SamlConfigPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cfg, setCfg] = useState<SamlConfig | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [entityId, setEntityId] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  const [sloUrl, setSloUrl] = useState('');
  const [certificate, setCertificate] = useState('');
  const [nameIdFormat, setNameIdFormat] = useState('urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress');
  const [defaultRoles, setDefaultRoles] = useState('member');

  const acsUrl = `${API_ORIGIN}/v1/auth/saml2/callback`;
  const spEntityId = `${API_ORIGIN}/saml2/metadata`;

  useEffect(() => {
    setLoading(true);
    apiGet<{ config: SamlConfig }>('/v1/admin/saml/config')
      .then(d => {
        const c = d.config;
        setCfg(c);
        setEnabled(c.enabled ?? false);
        setEntityId(c.entity_id ?? '');
        setSsoUrl(c.sso_url ?? '');
        setSloUrl(c.slo_url ?? '');
        setNameIdFormat(c.name_id_format ?? 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress');
        setDefaultRoles((c.default_roles ?? ['member']).join(', '));
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload: Record<string, unknown> = {
        enabled, entityId, ssoUrl, sloUrl, nameIdFormat,
        defaultRoles: defaultRoles.split(',').map(r => r.trim()).filter(Boolean),
      };
      if (certificate) payload['certificate'] = certificate;
      await apiMutate('/v1/admin/saml/config', 'PUT', payload);
      setSuccess('SAML 2.0 configuration saved.');
      setCertificate('');
    } catch (e) { setError(String(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-nord-4 p-4">Loading configuration…</div>;

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-nord-15/15 flex items-center justify-center text-lg">🏛️</div>
        <div className="flex-1">
          <h3 className="font-semibold text-nord-6">SAML 2.0 / eIDAS</h3>
          <p className="text-xs text-nord-4">SAML federation for government and enterprise IdPs</p>
        </div>
        <StatusBadge enabled={enabled} />
      </div>

      <ErrorBanner error={error} />
      <SuccessBanner message={success} />

      <div className={`${cardCls} flex items-center gap-3`}>
        <div className="flex-1">
          <div className="text-sm font-medium text-nord-6">Enable SAML 2.0 Sign-in</div>
          <div className="text-xs text-nord-4 mt-0.5">Allow users to authenticate via SAML 2.0 identity providers (AD FS, Keycloak, eIDAS nodes)</div>
        </div>
        <ToggleSwitch enabled={enabled} onChange={() => setEnabled(e => !e)} />
      </div>

      {/* SP (Eurion) metadata — read-only info */}
      <div className={`${cardCls} space-y-3`}>
        <h4 className="text-sm font-medium text-nord-5">Service Provider (Eurion) Metadata</h4>
        <p className="text-xs text-nord-4">Provide these values to your Identity Provider administrator:</p>
        <div>
          <label className={labelCls}>Entity ID / Issuer</label>
          <div className="w-full bg-nord-0 border border-nord-2 rounded-lg px-3 py-2 text-sm text-nord-6 font-mono select-all">{spEntityId}</div>
        </div>
        <div>
          <label className={labelCls}>Assertion Consumer Service URL (ACS)</label>
          <div className="w-full bg-nord-0 border border-nord-2 rounded-lg px-3 py-2 text-sm text-nord-6 font-mono select-all">{acsUrl}</div>
        </div>
        <div>
          <label className={labelCls}>NameID Format</label>
          <div className="w-full bg-nord-0 border border-nord-2 rounded-lg px-3 py-2 text-sm text-nord-4 font-mono text-xs">{nameIdFormat}</div>
        </div>
      </div>

      {/* IdP configuration */}
      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">Identity Provider Configuration</h4>
        <div>
          <label className={labelCls}>IdP Entity ID *</label>
          <input className={inputCls} value={entityId} onChange={e => setEntityId(e.target.value)}
            placeholder="https://idp.example.com/saml/metadata" />
          <p className="text-xs text-nord-3 mt-1">The entityID from the IdP's SAML metadata</p>
        </div>
        <div>
          <label className={labelCls}>SSO URL (HTTP-Redirect) *</label>
          <input className={inputCls} value={ssoUrl} onChange={e => setSsoUrl(e.target.value)}
            placeholder="https://idp.example.com/saml/sso" />
          <p className="text-xs text-nord-3 mt-1">SingleSignOnService URL with HTTP-Redirect binding</p>
        </div>
        <div>
          <label className={labelCls}>SLO URL (optional)</label>
          <input className={inputCls} value={sloUrl} onChange={e => setSloUrl(e.target.value)}
            placeholder="https://idp.example.com/saml/slo" />
          <p className="text-xs text-nord-3 mt-1">Single Logout Service URL</p>
        </div>
        <div>
          <label className={labelCls}>
            IdP Signing Certificate (PEM) {cfg?.has_certificate ? <span className="text-nord-14">(uploaded — paste to replace)</span> : <span className="text-nord-11">(not set)</span>}
          </label>
          <textarea className={`${inputCls} h-28 font-mono text-xs`} value={certificate} onChange={e => setCertificate(e.target.value)}
            placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDqDCCApCgAwIBAgI...&#10;-----END CERTIFICATE-----" />
          <p className="text-xs text-nord-3 mt-1">PEM-encoded X.509 certificate for verifying SAML assertions</p>
        </div>
      </div>

      <div className={`${cardCls} space-y-4`}>
        <h4 className="text-sm font-medium text-nord-5">User Provisioning</h4>
        <div>
          <label className={labelCls}>NameID Format</label>
          <select className={inputCls} value={nameIdFormat} onChange={e => setNameIdFormat(e.target.value)}>
            <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress">Email Address</option>
            <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:persistent">Persistent</option>
            <option value="urn:oasis:names:tc:SAML:2.0:nameid-format:transient">Transient</option>
            <option value="urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified">Unspecified</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Default Roles (comma-separated)</label>
          <input className={inputCls} value={defaultRoles} onChange={e => setDefaultRoles(e.target.value)} placeholder="member" />
        </div>
      </div>

      <div className="bg-nord-0 border border-nord-2 rounded-lg p-4 text-xs text-nord-4 space-y-1">
        <div className="font-medium text-nord-5 mb-2">eIDAS Compliance</div>
        <div>• SAML 2.0 assertions are validated using the IdP signing certificate</div>
        <div>• Supports eIDAS node integration via standard SAML 2.0 SP protocol</div>
        <div>• Attribute mapping: <code className="text-nord-8">email</code>, <code className="text-nord-8">givenName</code>, <code className="text-nord-8">surname</code>, <code className="text-nord-8">PersonIdentifier</code></div>
        <div>• EU-sovereign: all assertion processing happens within Eurion infrastructure</div>
      </div>

      <button onClick={save} disabled={saving}
        className="px-5 py-2.5 bg-nord-10 hover:bg-nord-10/80 text-nord-6 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
        {saving ? 'Saving…' : 'Save Configuration'}
      </button>
    </div>
  );
}

function SamlUsersPanel() {
  const [users, setUsers] = useState<ProvisionedUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    apiGet<{ items: ProvisionedUser[]; total: number }>(`/v1/admin/saml/users?q=${encodeURIComponent(search)}&limit=50`)
      .then(d => { setUsers(d.items); setTotal(d.total); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [search]);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-nord-6">SAML 2.0 Users</h3>
          <p className="text-xs text-nord-4 mt-0.5">{total} user{total !== 1 ? 's' : ''} provisioned via SAML</p>
        </div>
        <input className={`${inputCls} w-56`} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <ErrorBanner error={error} />

      {loading ? (
        <div className="text-nord-4 text-sm p-4">Loading…</div>
      ) : users.length === 0 ? (
        <div className="text-center text-nord-4 text-sm py-10 bg-nord-1 border border-nord-2 rounded-xl">
          <div className="text-2xl mb-2">🏛️</div>
          No SAML users found.
        </div>
      ) : (
        <div className="bg-nord-1 border border-nord-2 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nord-2 text-left text-xs text-nord-4">
                <th className="px-4 py-3 font-medium">User</th>
                <th className="px-4 py-3 font-medium">Roles</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Provisioned</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-b border-nord-2/50 hover:bg-nord-2/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-nord-15/20 flex items-center justify-center text-nord-15 text-sm font-medium">
                        {(u.display_name ?? u.email)[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div>
                        <div className="text-nord-6 font-medium text-sm">{u.display_name ?? u.email}</div>
                        <div className="text-nord-4 text-xs">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.user_roles ?? []).map(r => (
                        <span key={r} className="px-1.5 py-0.5 bg-nord-15/15 text-nord-15 rounded text-xs">{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.user_status === 'active' ? 'bg-nord-14/15 text-nord-14' : 'bg-nord-3/30 text-nord-4'}`}>
                      {u.user_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-nord-4">{new Date(u.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

type Provider = 'azure' | 'google' | 'saml';
type SubTab = 'config' | 'mappings' | 'users';

export default function IdentityProviders() {
  const [provider, setProvider] = useState<Provider>('azure');
  const [tab, setTab] = useState<SubTab>('config');

  const providers: { id: Provider; label: string; sub: string; icon: string; color: string }[] = [
    { id: 'azure', label: 'Microsoft Azure AD', sub: 'Entra ID · OIDC', icon: '🔷', color: 'nord-8' },
    { id: 'google', label: 'Google Workspace', sub: 'OIDC / OAuth2', icon: '🔐', color: 'nord-14' },
    { id: 'saml', label: 'SAML 2.0 / eIDAS', sub: 'SAML Federation', icon: '🏛️', color: 'nord-15' },
  ];

  const azureTabs: { id: SubTab; label: string }[] = [
    { id: 'config', label: '⚙️ Configuration' },
    { id: 'mappings', label: '🗂️ Group Mappings' },
    { id: 'users', label: '👥 Users' },
  ];

  const standardTabs: { id: SubTab; label: string }[] = [
    { id: 'config', label: '⚙️ Configuration' },
    { id: 'users', label: '👥 Users' },
  ];

  const currentTabs = provider === 'azure' ? azureTabs : standardTabs;

  // Reset to config tab when switching providers
  const switchProvider = (p: Provider) => {
    setProvider(p);
    setTab('config');
  };

  const renderContent = () => {
    if (provider === 'azure') {
      if (tab === 'config') return <AzureConfigPanel />;
      if (tab === 'mappings') return <AzureGroupMappingsPanel />;
      if (tab === 'users') return <AzureUsersPanel />;
    }
    if (provider === 'google') {
      if (tab === 'config') return <GoogleConfigPanel />;
      if (tab === 'users') return <GoogleUsersPanel />;
    }
    if (provider === 'saml') {
      if (tab === 'config') return <SamlConfigPanel />;
      if (tab === 'users') return <SamlUsersPanel />;
    }
    return null;
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      {/* Page header */}
      <div>
        <h2 className="text-xl font-semibold text-nord-6">Identity Providers</h2>
        <p className="text-sm text-nord-4 mt-1">Configure external identity providers for single sign-on (SSO).</p>
      </div>

      {/* Provider selector badges */}
      <div className="flex gap-3 flex-wrap">
        {providers.map(p => (
          <button
            key={p.id}
            onClick={() => switchProvider(p.id)}
            className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-all text-left ${
              provider === p.id
                ? 'bg-nord-1 border-nord-8 shadow-sm'
                : 'bg-nord-1/50 border-nord-2 hover:border-nord-3'
            }`}
          >
            <span className="text-xl">{p.icon}</span>
            <div>
              <div className={`text-sm font-medium ${provider === p.id ? 'text-nord-6' : 'text-nord-4'}`}>{p.label}</div>
              <div className="text-xs text-nord-3">{p.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Sub-tab navigation */}
      <div className="border-b border-nord-2">
        <nav className="flex gap-1">
          {currentTabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? 'border-nord-8 text-nord-6'
                  : 'border-transparent text-nord-4 hover:text-nord-5'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>{renderContent()}</div>
    </div>
  );
}
